/**
 * The voice-sample dialog as a machine (ADR-0062): instructions → the room check → recording →
 * review → saving → stored, where the page's news of the capture reads *being computed*,
 * *stored* — with the server's verdict on the sample's quality once the page knows it — *too
 * little speech* or *failed*; and closed, where one opening of the dialog ends and nothing is
 * held any more. It owns the one thing the dialog holds — the take — and decides what each phase
 * offers, what closing would lose, whom a take is sent for, and which taps count. The recording
 * session and the upload report to it; the hook `useVoiceSampleFlow` runs them. Pure.
 */
import { maySendAgain } from '@/components/humint/questioning/recording-types'
import type { CaptureState } from '@/components/humint/types'
import { canKeepVoiceSample } from '@/components/humint/voice/voice-rules'
import {
  mayRecordBetter,
  sameQuality,
  type VoiceSampleQuality,
} from '@/components/humint/voice/voice-sample-quality'
import type {
  VoiceMicrophoneProblem,
  VoiceRoom,
  VoiceSampleMode,
  VoiceSampleView,
  VoiceSaveProblem,
} from '@/components/humint/voice/voice-types'
import type { VoiceSessionView } from './voice-sample-session'
import { takeSummary, type VoiceSampleIntake, type VoiceSampleTake } from './voice-sample-take'
import type { VoiceSampleTarget, VoiceSendOutcome } from './voice-sample-upload'

/** How a stored sample's job ended; null while it runs. */
export type VoiceSampleOutcome = 'done' | 'no_speech' | 'failed'

const OUTCOME_OF: Partial<Record<CaptureState, VoiceSampleOutcome>> = {
  done: 'done',
  no_speech: 'no_speech',
  failed: 'failed',
}

/**
 * How the job of a voice sample ended, from the state the page reports of its capture: `done`
 * once its voiceprint is kept at the subject, `no_speech` with too little speech in it, `failed`.
 * Null while it runs, and for a capture the page does not know (yet).
 */
export function voiceOutcomeOf(state: CaptureState | null | undefined): VoiceSampleOutcome | null {
  return state ? (OUTCOME_OF[state] ?? null) : null
}

/** What a phase lets the operator do; everything else is not offered. */
export type VoiceFlowOffer =
  | 'start'
  | 'goOn'
  | 'stop'
  | 'startOver'
  | 'recordAgain'
  | 'save'
  | 'retry'

/** A take that went to the server, and whom it went for: every send of it repeats the first. */
interface SentTake {
  take: VoiceSampleTake
  target: VoiceSampleTarget
}

export type VoiceFlowState =
  | { phase: 'instructions'; starting: boolean; problem: VoiceMicrophoneProblem | null }
  /**
   * The microphone is open (or opening again) and the room is checked before anything is
   * counted. `loud`: it was found too loud, and the count waits for the operator.
   */
  | { phase: 'room'; check: 'measuring' | 'loud' }
  /** `room`: what the check found; null where it could not tell, absent where none was made. */
  | { phase: 'recording'; room?: VoiceRoom | null }
  | { phase: 'review'; take: VoiceSampleTake }
  /**
   * `progress` 0..1 of the bytes sent, null while the take is hashed. `rerun`: the take is stored
   * already and its job failed — it goes again to run the job again.
   */
  | ({ phase: 'saving'; progress: number | null; rerun: boolean } & SentTake)
  | ({ phase: 'send_failed'; problem: VoiceSaveProblem; rerun: boolean } & SentTake)
  /**
   * The server has it. `outcome`: what the page reports of its job, null while it runs. `stale`:
   * the outcome the page still reports of the job before a rerun, which is no news of the rerun.
   * `quality`: the server's verdict on a sample whose voiceprint is kept, once the page knows it.
   */
  | ({
      phase: 'stored'
      captureId: string
      outcome: VoiceSampleOutcome | null
      stale: VoiceSampleOutcome | null
      quality: VoiceSampleQuality | null
    } & SentTake)
  /**
   * The dialog was closed: one opening ends here. It holds nothing any more — no take to play, to
   * send or to bring back — and goes on drawing what it drew (`shown`, `offers`) while it goes.
   */
  | { phase: 'closed'; shown: VoiceSampleView; offers: readonly VoiceFlowOffer[] }

export type VoiceFlowEvent =
  | { type: 'imported'; take: VoiceSampleTake }
  | { type: 'chooseSource' }
  /** The recording session said its phase. */
  | { type: 'session'; view: VoiceSessionView }
  /** A recording ended. */
  | { type: 'sampled'; intake: VoiceSampleIntake }
  /** *Record again*: what the dialog holds is dropped and the microphone is asked for. */
  | { type: 'restart' }
  /**
   * *Save* or *Try again*: the take goes to the server. `target`: whom the page names now — a
   * take that went before goes for whom it went then (`sendTarget`).
   */
  | { type: 'send'; target: VoiceSampleTarget }
  | { type: 'progress'; progress: number | null }
  | { type: 'sent'; outcome: Exclude<VoiceSendOutcome, { kind: 'cancelled' }> }
  /**
   * What the page knows of the stored capture's job. `fresh`: read after the rerun was stored
   * for long enough that the page has asked again. `quality`: the server's verdict on the sample,
   * where the page knows one.
   */
  | {
      type: 'reported'
      outcome: VoiceSampleOutcome | null
      fresh?: boolean
      quality?: VoiceSampleQuality | null
    }
  /**
   * The dialog closes — its X, *Done*, the page behind it, or a kept take handed back (`collect`)
   * — showing this.
   */
  | { type: 'closed'; shown: VoiceSampleView; offers: readonly VoiceFlowOffer[] }

export const INITIAL_VOICE_FLOW: VoiceFlowState = {
  phase: 'instructions',
  starting: false,
  problem: null,
}

/** The session checks the room: the dialog measures with it, or waits where it is too loud. */
function onChecking(state: VoiceFlowState, room: VoiceSessionView['room']): VoiceFlowState {
  const check = room === 'loud' ? 'loud' : 'measuring'
  return state.phase === 'room' && state.check === check ? state : { phase: 'room', check }
}

/** The session counts the speech: the dialog records, knowing what the room check found. */
function onCounting(state: VoiceFlowState, room: VoiceSessionView['room']): VoiceFlowState {
  return state.phase === 'recording' && state.room === room ? state : { phase: 'recording', room }
}

/** What the session's phase means before a recording, and while one runs. */
function onSession(state: VoiceFlowState, view: VoiceSessionView): VoiceFlowState {
  const { phase } = state
  // A dialog that holds a take has no recording under way: nothing of the session changes it.
  if (phase !== 'instructions' && phase !== 'room' && phase !== 'recording') return state
  if (view.phase === 'checking') return onChecking(state, view.room)
  if (view.phase === 'recording') return onCounting(state, view.room)
  if (phase === 'instructions') {
    return { phase: 'instructions', starting: view.phase === 'starting', problem: view.problem }
  }
  // A start-over opens the microphone again: the ring stays, at zero, unless that fails.
  if (view.phase === 'idle')
    return { phase: 'instructions', starting: false, problem: view.problem }
  return state
}

function onSampled(state: VoiceFlowState, intake: VoiceSampleIntake): VoiceFlowState {
  // A recording may end while its room is still checked: at its longest length, or interrupted.
  if (state.phase !== 'recording' && state.phase !== 'room') return state
  if (intake.kind === 'taken') return { phase: 'review', take: intake.take }
  return { phase: 'instructions', starting: false, problem: intake.problem }
}

/**
 * Whom a take is sent for. Its first send names them as the page does then; every send after it
 * — *Try again*, a failed job run again — repeats the first, whatever the page names by now. The
 * server keeps the capture under its id with the encounter it came with, and answers the same id
 * with another encounter as a conflict.
 */
export function sendTarget(state: VoiceFlowState, named: VoiceSampleTarget): VoiceSampleTarget {
  return 'target' in state ? state.target : named
}

const canSendTake = (take: VoiceSampleTake): boolean =>
  canKeepVoiceSample({ durationMs: take.audio.durationMs, fileName: take.fileName })

function onSend(state: VoiceFlowState, named: VoiceSampleTarget): VoiceFlowState {
  const { phase } = state
  const sent = { progress: null, target: sendTarget(state, named) }
  if (phase === 'review') {
    if (!canSendTake(state.take)) return state
    return { phase: 'saving', take: state.take, ...sent, rerun: false }
  }
  if (phase === 'send_failed') {
    return { phase: 'saving', take: state.take, ...sent, rerun: state.rerun }
  }
  if (phase === 'stored' && state.outcome === 'failed') {
    return { phase: 'saving', take: state.take, ...sent, rerun: true }
  }
  return state
}

function onSent(
  state: VoiceFlowState,
  outcome: Extract<VoiceFlowEvent, { type: 'sent' }>['outcome']
): VoiceFlowState {
  if (state.phase !== 'saving') return state
  const { take, target, rerun } = state
  if (outcome.kind === 'failed') {
    return { phase: 'send_failed', take, target, problem: outcome.problem, rerun }
  }
  const { captureId } = outcome
  const stale = rerun ? 'failed' : null
  return { phase: 'stored', take, target, captureId, outcome: null, stale, quality: null }
}

function onReported(
  state: VoiceFlowState,
  { outcome, fresh = false, quality = null }: Extract<VoiceFlowEvent, { type: 'reported' }>
): VoiceFlowState {
  if (state.phase !== 'stored') return state
  // The job runs: what the page said of the run before is past. An outcome already shown stays.
  if (outcome === null) return state.stale ? { ...state, stale: null } : state
  if (!fresh && outcome === state.stale) return state
  // Only a kept voiceprint is judged, and a verdict once shown stays: a capture's never changes.
  const judged = outcome === 'done' ? (quality ?? state.quality) : null
  const news = outcome !== state.outcome || state.stale !== null
  if (!news && sameQuality(judged, state.quality)) return state
  return { ...state, outcome, stale: null, quality: judged }
}

export function voiceFlowReducer(state: VoiceFlowState, event: VoiceFlowEvent): VoiceFlowState {
  // One opening ends when the dialog closes: nothing that arrives late opens it again.
  if (state.phase === 'closed') return state
  switch (event.type) {
    case 'imported':
      return state.phase === 'instructions' && !state.starting
        ? { phase: 'review', take: event.take }
        : state
    case 'chooseSource':
      return state.phase === 'saving' ? state : INITIAL_VOICE_FLOW
    case 'session':
      return onSession(state, event.view)
    case 'sampled':
      return onSampled(state, event.intake)
    case 'restart':
      // A fresh recording begins with its room: the ring stands at zero while it is checked.
      return state.phase === 'saving' ? state : { phase: 'room', check: 'measuring' }
    case 'send':
      return onSend(state, event.target)
    case 'progress':
      // The upload reports many times a second; a progress the screen already shows is no news.
      if (state.phase !== 'saving' || state.progress === event.progress) return state
      return { ...state, progress: event.progress }
    case 'sent':
      return onSent(state, event.outcome)
    case 'reported':
      return onReported(state, event)
    case 'closed':
      return { phase: 'closed', shown: event.shown, offers: event.offers }
  }
}

/** The take the dialog holds, in every phase that has one. */
export function heldTake(state: VoiceFlowState): VoiceSampleTake | null {
  return 'take' in state ? state.take : null
}

/** The capture the sample was stored as, once it was. */
export function storedCaptureId(state: VoiceFlowState): string | null {
  return state.phase === 'stored' ? state.captureId : null
}

/** The dialog as the screens draw it. */
export function toVoiceSampleView(
  state: VoiceFlowState,
  wakeLock: 'held' | 'unsupported'
): VoiceSampleView {
  switch (state.phase) {
    case 'instructions':
      return state
    case 'room':
      return { phase: 'room', check: state.check, wakeLock }
    case 'recording':
      return { phase: 'recording', wakeLock, room: state.room }
    case 'review':
      return { phase: 'review', take: takeSummary(state.take) }
    case 'saving':
      return { phase: 'saving', take: takeSummary(state.take), progress: state.progress }
    case 'send_failed': {
      const failure = { kind: 'send', problem: state.problem } as const
      return { phase: 'failed', take: takeSummary(state.take), failure }
    }
    case 'stored': {
      const take = takeSummary(state.take)
      if (state.outcome === 'failed') {
        return { phase: 'failed', take, failure: { kind: 'processing' } }
      }
      if (state.outcome === 'done') return { phase: 'done', take, quality: state.quality }
      return { phase: state.outcome ?? 'processing', take }
    }
    case 'closed':
      return state.shown
  }
}

/**
 * A sample only this tab holds: one being recorded, or recorded and not stored. Leaving the page
 * loses it, so the page's leave guard counts it. A room still being checked holds nothing of the
 * subject yet.
 */
export function hasUnsavedTake(state: VoiceFlowState): boolean {
  const { phase } = state
  return (
    phase === 'recording' ||
    phase === 'review' ||
    (phase === 'saving' && !state.rerun) ||
    (phase === 'send_failed' && !state.rerun)
  )
}

/** A recorded sample that could still become a voiceprint: dropping it is asked about first. */
function holdsKeepableTake(state: VoiceFlowState): boolean {
  if (state.phase === 'review') return canSendTake(state.take)
  return state.phase === 'send_failed' && !state.rerun && maySendAgain(state.problem)
}

/** The dialog cannot be closed while the sample is on its way: nothing would say how it went. */
export const isClosable = (state: VoiceFlowState): boolean => state.phase !== 'saving'

/** Whether closing asks first: it would end a recording, or drop a sample that could be kept. */
export const closeAsks = (state: VoiceFlowState): boolean =>
  state.phase === 'recording' || holdsKeepableTake(state)

/** Whether *Record again* asks first: it would drop a sample that could be kept. */
export const recordAgainAsks = (state: VoiceFlowState): boolean => holdsKeepableTake(state)

const NOTHING: readonly VoiceFlowOffer[] = []

/**
 * `collect` hands the take back at *Use this sample*, so it never has anything to send again. A
 * closed dialog draws the buttons it had while it goes; none of them does anything.
 */
export function voiceFlowOffers(
  state: VoiceFlowState,
  mode: VoiceSampleMode
): readonly VoiceFlowOffer[] {
  switch (state.phase) {
    case 'instructions':
      // A browser that cannot record here has nothing to start.
      return state.problem === 'unsupported' ? NOTHING : ['start']
    case 'room':
      // Nothing to tap while the room is measured; where it is too loud, elsewhere or all the same.
      return state.check === 'loud' ? ['goOn', 'startOver'] : NOTHING
    case 'recording':
      return ['stop', 'startOver']
    case 'review':
      return canSendTake(state.take) ? ['save', 'recordAgain'] : ['recordAgain']
    case 'saving':
      return NOTHING
    case 'send_failed':
      return maySendAgain(state.problem) ? ['retry', 'recordAgain'] : ['recordAgain']
    case 'stored':
      if (mode === 'collect' || state.outcome === null) return NOTHING
      // A kept voiceprint needs nothing more — unless another recording could give a better one.
      if (state.outcome === 'done')
        return mayRecordBetter(state.quality) ? ['recordAgain'] : NOTHING
      return state.outcome === 'failed' ? ['retry', 'recordAgain'] : ['recordAgain']
    case 'closed':
      return state.offers
  }
}

/**
 * A tap counts only once its screen has stood this long. The buttons of a phase stand where those
 * of the phase before stood, so the second tap of a double tap — on its way when the screen
 * changed under the finger — was aimed at the button before, and so is a second Enter.
 */
export const VOICE_TAP_SETTLE_MS = 500

/**
 * The screen a view draws, as far as its buttons go: its phase — and, for the room check,
 * whether it measures or waits, each with a footer of its own.
 */
export type VoiceScreen = Exclude<VoiceSampleView['phase'], 'room'> | 'room:measuring' | 'room:loud'

export const voiceScreenOf = (view: VoiceSampleView): VoiceScreen =>
  view.phase === 'room' ? `room:${view.check}` : view.phase

/** Since when the dialog has shown the screen it shows; `at` on a monotonic clock, in ms. */
export interface ScreenArrival {
  phase: VoiceScreen
  at: number
}

/** The arrival after one more look at the screen: a new one only when the screen has changed. */
export function screenArrival(
  last: ScreenArrival | null,
  phase: VoiceScreen,
  now: number
): ScreenArrival {
  return last?.phase === phase ? last : { phase, at: now }
}

/** Whether a tap at `now` was aimed at the screen that is shown. */
export const tapCounts = (arrival: ScreenArrival, now: number): boolean =>
  now - arrival.at >= VOICE_TAP_SETTLE_MS
